import React, { useState, useEffect, useMemo } from 'react';

const DentalImportance: React.FC = () => {
  const [currentStat, setCurrentStat] = useState(0);
  const [animatedCount, setAnimatedCount] = useState(0);
  
  const stats = useMemo(() => [
    {
      number: 85,
      suffix: '%',
      text: 'of pets over 3 years old have periodontal disease',
      icon: 'fas fa-exclamation-triangle',
      color: 'text-red-500'
    },
    {
      number: 80,
      suffix: '%',
      text: 'of dental disease occurs below the gumline',
      icon: 'fas fa-eye-slash',
      color: 'text-orange-500'
    },
    {
      number: 5,
      suffix: ' years',
      text: 'can be added to your pet\'s life with proper dental care',
      icon: 'fas fa-heart',
      color: 'text-red-400'
    }
  ], []);

  const hiddenSigns = [
    {
      icon: 'fas fa-tooth',
      title: 'Tooth Pain',
      description: 'Your pet suffers in silence from painful dental disease'
    },
    {
      icon: 'fas fa-heartbeat',
      title: 'Organ Damage',
      description: 'Bacteria from the mouth travels to heart, liver, and kidneys'
    },
    {
      icon: 'fas fa-utensils',
      title: 'Eating Problems',
      description: 'Difficulty chewing leads to poor nutrition and weight loss'
    }
  ];

  // Animate the statistics counter
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % stats.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [stats.length]);

  // Animate the number counting up
  useEffect(() => {
    const target = stats[currentStat].number;
    const increment = target / 50;
    let current = 0;
    
    const countTimer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setAnimatedCount(target);
        clearInterval(countTimer);
      } else {
        setAnimatedCount(Math.floor(current));
      }
    }, 30);

    return () => clearInterval(countTimer);
  }, [currentStat, stats]);

  return (
    <section id="about" className="bg-gradient-to-br from-red-50 to-orange-50 py-12 lg:py-16 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-10 right-10 w-32 h-32 bg-red-200 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute bottom-10 left-10 w-24 h-24 bg-orange-200 rounded-full opacity-20 animate-bounce-gentle"></div>
      </div>
      
      <div className="container-custom relative z-10">
        {/* Two-Column Desktop Layout */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
          
          {/* Left Column: Intro & Stats */}
          <div>
            <div className="text-center lg:text-left mb-8 lg:mb-10">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-hellotend-brand-brown mb-4 lg:mb-6 leading-[1.1] font-nantes" style={{fontWeight: 700}}>
                The Hidden Crisis in Your Pet's Mouth
              </h2>
              <p className="text-lg text-hellotend-neutral-700 font-founders leading-relaxed" style={{fontWeight: 400}}>
                Many people don't realize their pet is suffering from painful dental disease. Learn about our{' '}
                <a href="/services" className="text-hellotend-brand-blue hover:underline font-medium">veterinary dental services</a>.
              </p>
            </div>
            
            {/* Animated Statistics - Compact */}
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 text-center mb-8 lg:mb-0 border border-orange-100">
              <div className="mb-6">
                <div className="text-5xl lg:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500 leading-none">
                  {animatedCount}
                  <span className="text-2xl lg:text-3xl ml-1">
                    {stats[currentStat].suffix}
                  </span>
                </div>
                <div className="flex flex-col items-center justify-center gap-2 mt-4">
                  <i className={`${stats[currentStat].icon} ${stats[currentStat].color} text-3xl`}></i>
                  <p className="text-lg lg:text-xl text-gray-700 font-medium">
                    {stats[currentStat].text}
                  </p>
                </div>
              </div>
              
              {/* Progress indicators */}
              <div className="flex justify-center gap-2">
                {stats.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      index === currentStat ? 'bg-brand-teal scale-125' : 'bg-gray-300'
                    }`}
                  ></div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Right Column: Problems Grid */}
          <div>
            <h3 className="text-2xl md:text-3xl font-bold text-center lg:text-left text-hellotend-brand-brown mb-6 lg:mb-8 leading-[1.1] font-nantes" style={{fontWeight: 700}}>
              Is Your Pet Suffering?
            </h3>
            
            <div className="grid gap-6">
              {/* Signs you will see - Compact Row */}
              <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-red-400">
                <h4 className="text-lg font-bold text-hellotend-brand-brown mb-3 font-nantes flex items-center gap-2">
                  <i className="fas fa-eye text-red-400"></i>
                  Signs you will see
                </h4>
                <div className="flex items-start gap-4">
                   <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-frown text-xl text-red-500"></i>
                    </div>
                    <div>
                      <h5 className="font-bold text-base text-primary mb-1">Bad Breath</h5>
                      <p className="text-gray-600 text-sm leading-snug">
                        Foul odor caused by harmful bacteria living under the gums.
                      </p>
                    </div>
                </div>
              </div>

              {/* Signs you may not see - Compact List */}
              <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-orange-400">
                <h4 className="text-lg font-bold text-hellotend-brand-brown mb-3 font-nantes flex items-center gap-2">
                  <i className="fas fa-eye-slash text-orange-400"></i>
                  Signs you may not see
                </h4>
                <div className="grid gap-4">
                  {hiddenSigns.map((sign, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`${sign.icon} text-lg text-orange-500`}></i>
                      </div>
                      <div>
                        <h5 className="font-bold text-sm text-primary">{sign.title}</h5>
                        <p className="text-gray-600 text-xs lg:text-sm leading-snug">{sign.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DentalImportance;